import React from "react";
import { Link } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import { useSWRConfig } from "swr";
import { putFetcher } from "api/swr";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/use-toast";
import {
  ArrowUpIcon,
  AvatarIcon,
  Cross2Icon,
  DotsHorizontalIcon,
  Pencil1Icon,
} from "@radix-ui/react-icons";
import DisplayUpdateTime from "ui/DisplayUpdateTime";
import { AvatarMediaPreview } from "AdminPanel/SpineAvatars/SpineAvatars/components/AvatarMediaPreview";

const StickyTop = ({ row }: { row: any }) => {
  const data = row.original;
  const { mutate } = useSWRConfig();
  const { trigger, isMutating } = useSWRMutation(
    `/spine-frames/${data.id}`,
    putFetcher,
    {
      onSuccess: () => {
        void mutate(
          (key) => Array.isArray(key) && key[0] === "/spine-frames",
        );
      },
    },
  );

  const handleClick = (e: Event) => {
    e.preventDefault();
    void trigger({ key: "stickyTop" });
  };

  return (
    <DropdownMenuItem onSelect={handleClick}>
      <ArrowUpIcon className="mr-2 h-4 w-4" />
      <span>Sticky Top{isMutating ? "..." : null}</span>
    </DropdownMenuItem>
  );
};

const Offline = ({ row }: { row: any }) => {
  const data = row.original;
  const { mutate } = useSWRConfig();
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);

  const { trigger, isMutating } = useSWRMutation(
    `/spine-frames/${data.id}`,
    putFetcher,
    {
      onSuccess: () => {
        void mutate(
          (key) => Array.isArray(key) && key[0] === "/spine-frames",
        );
      },
    },
  );

  const handleOpen = (e: Event) => {
    e.preventDefault();
    setOpen(true);
  };

  const handleConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      await trigger({ key: "offline" });
      toast({
        title: "Success",
        description: "offline successfully",
        className: "bg-green-500 text-white",
      });
      setOpen(false);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "未知错误";
      toast({
        title: "Error",
        description: "Offline failed: " + message,
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <DropdownMenuItem className="text-red-500" onSelect={handleOpen}>
        <Cross2Icon className="mr-2 h-4 w-4" />
        <span>offline{isMutating ? "..." : null}</span>
      </DropdownMenuItem>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Make sure to set offline</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isMutating}
            >
              {isMutating ? "Processing..." : "Confirm"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const DataTableRowActions = ({ row }: { row: any }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
      >
        <DotsHorizontalIcon className="h-4 w-4" />
        <span className="sr-only">Open menu</span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-[180px]">
      <DropdownMenuLabel>Actions</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild>
        <Link to={`/admin/spine/editor/${row.original.id}`}>
          <Pencil1Icon className="mr-2 h-4 w-4" />
          Editor
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to={`/admin/spine-avatars?tid=${encodeURIComponent(row.original.id)}`}>
          <AvatarIcon className="mr-2 h-4 w-4" />
          Avatars
        </Link>
      </DropdownMenuItem>
      <StickyTop row={row} />
      <Offline row={row} />
    </DropdownMenuContent>
  </DropdownMenu>
);

export const columns = [
  {
    header: "#",
    cell: ({ row }: { row: { index: number } }) => row.index + 1,
  },
  {
    header: "Cover",
    accessorKey: "cover",
    cell: ({ row }: { row: any }) => {
      const cover = row.original.cover;
      const videoCover =
        row.original.videoCoverPreview ||
        row.original.videoCover ||
        row.original.coverVideo;
      return cover || videoCover ? (
        <div className="aspect-square rounded-md h-24 w-24 overflow-hidden border bg-black">
          <AvatarMediaPreview
            videoUrl={videoCover}
            imageUrl={cover}
            className="h-full w-full"
          />
        </div>
      ) : (
        <div className="aspect-square rounded-md bg-muted h-24 w-24 flex items-center justify-center text-xs text-muted-foreground">
          -
        </div>
      );
    },
  },
  {
    header: "Preview",
    accessorKey: "preview",
    cell: ({ row }: { row: any }) => {
      const preview = row.original.preview;
      const videoPreview =
        row.original.videoPreviewPreview ||
        row.original.videoPreview ||
        row.original.previewCover ||
        row.original.videoCoverPreview ||
        row.original.videoCover;
      return preview || videoPreview ? (
        <div className="aspect-square rounded-md h-24 w-24 overflow-hidden border bg-black">
          <AvatarMediaPreview
            videoUrl={videoPreview}
            imageUrl={preview}
            className="h-full w-full"
          />
        </div>
      ) : (
        <div className="aspect-square rounded-md bg-muted h-24 w-24 flex items-center justify-center text-xs text-muted-foreground">
          -
        </div>
      );
    },
  },
  {
    header: "Name",
    accessorKey: "name",
    cell: ({ row }: { row: any }) => (
      <>
        <div className="fw-bold">{row.original.name}</div>
        <div className="text-black/90 text-xs">{row.original.id}</div>
        <p className="text-ellipsis w-56 text-black/60 text-xs line-clamp-2">
          {row.original.intro}
        </p>
      </>
    ),
  },
  {
    header: "Type",
    accessorKey: "type",
    cell: ({ row }: { row: any }) => {
      const type = row.original.type || "-";
      return <span className="text-sm">{type}</span>;
    },
  },
  {
    header: "Langs",
    accessorKey: "contentLangs",
    cell: ({ row }: { row: any }) => {
      const langs = row.original.contentLangs;
      if (!Array.isArray(langs) || langs.length === 0) {
        return <span className="text-muted-foreground text-xs">offline</span>;
      }
      return (
        <span className="text-xs text-muted-foreground line-clamp-2">
          {langs.join(", ")}
        </span>
      );
    },
  },
  {
    header: "Products",
    accessorKey: "productIds",
    cell: ({ row }: { row: any }) => {
      const ids = row.original.productIds;
      if (!Array.isArray(ids) || ids.length === 0) {
        return <span className="text-muted-foreground text-xs">—</span>;
      }
      return (
        <span className="text-xs text-muted-foreground line-clamp-2">
          {ids.join(", ")}
        </span>
      );
    },
  },
  {
    header: "Publisher",
    accessorKey: "publisher",
    cell: ({ row }: { row: any }) =>
      row.original.publisher ? (
        <span>{row.original.publisher}</span>
      ) : (
        <span className="text-muted-foreground">-</span>
      ),
  },
  {
    header: "PublishTime",
    accessorKey: "publishTime",
    cell: ({ row }: { row: any }) => (
      <DisplayUpdateTime updateTime={row.original.publishTime} />
    ),
  },
  {
    header: "Actions",
    cell: ({ row }: { row: any }) => <DataTableRowActions row={row} />,
  },
];
